import { Routes, Route } from 'react-router-dom'
import { LazyMotion, domAnimation } from 'motion/react'
import { FeedbackProvider } from '@/components/ui/feedback'

// Layouts
import MainLayout from './layouts/MainLayout'

// Pages — statically imported (NOT React.lazy): the prerenderer renders through
// renderToString, which is synchronous and cannot await a lazy() chunk, so lazy
// routes would SSR the Suspense fallback instead of real content. Static imports
// keep every route crawlable. See docs/conventions/frontend.md + scripts/prerender-bespoke.mjs.
import HomePage from './pages/HomePage'
import AboutPage from './pages/AboutPage'
import ContactPage from './pages/ContactPage'
import GetStartedPage from './pages/GetStartedPage'
import BookPage from './pages/BookPage'
import CancelBookingPage from './pages/CancelBookingPage'
import WhoWeHelpPage from './pages/WhoWeHelpPage'
import HowWeMeasurePage from './pages/HowWeMeasurePage'
import PayPage from './pages/PayPage'
import LinkInBioPage from './pages/LinkInBioPage'
import ChecklistPage from './pages/ChecklistPage'
import RequestsPage from './pages/RequestsPage'
import WelcomePage from './pages/WelcomePage'
import CallPage from './pages/CallPage'

// Packages
import PackagesPage from './pages/packages/PackagesPage'

// Portfolio
import PortfolioPage from './pages/portfolio/PortfolioPage'
import PortfolioItemPage from './pages/portfolio/PortfolioItemPage'

// Blog
import BlogPage from './pages/blog/BlogPage'
import BlogPostPage from './pages/blog/BlogPostPage'
import ChromaFestivalCaseStudyPage from './pages/blog/ChromaFestivalCaseStudyPage'
import FestivalsLosingApplicantsPage from './pages/blog/FestivalsLosingApplicantsPage'
import SierraCanyonCaseStudyPage from './pages/blog/SierraCanyonCaseStudyPage'
import HolyTrinityCaseStudyPage from './pages/blog/HolyTrinityCaseStudyPage'
import BrassCityJazzCaseStudyPage from './pages/blog/BrassCityJazzCaseStudyPage'
import WendellWhalumCaseStudyPage from './pages/blog/WendellWhalumCaseStudyPage'
import GreatHostComposersCaseStudyPage from './pages/blog/GreatHostComposersCaseStudyPage'
import BestWebsitePlatformsPage from './pages/blog/BestWebsitePlatformsPage'

// Services
import ServicesPage from './pages/services/ServicesPage'
import DigitalSystemsPage from './pages/services/DigitalSystemsPage'
import WebsitesPage from './pages/services/WebsitesPage'
import DigitalPartnershipPage from './pages/services/DigitalPartnershipPage'
import DoneForYouPage from './pages/services/DoneForYouPage'
import MusicManagerPage from './pages/MusicManagerPage'
import EmailMarketingPage from './pages/services/EmailMarketingPage'
import DonorManagementPage from './pages/services/DonorManagementPage'
import TicketingPage from './pages/services/TicketingPage'
import ApplicationsPage from './pages/services/ApplicationsPage'
import GraphicDesignPage from './pages/services/GraphicDesignPage'
import PhotographyPage from './pages/services/PhotographyPage'

// Platform
import PlatformPage from './pages/platform/PlatformPage'
import DonationsFeaturePage from './pages/platform/features/DonationsFeaturePage'
import WebsiteFeaturePage from './pages/platform/features/WebsiteFeaturePage'
import TeamFeaturePage from './pages/platform/features/TeamFeaturePage'
import EventsFeaturePage from './pages/platform/features/EventsFeaturePage'
import ApplicationsFeaturePage from './pages/platform/features/ApplicationsFeaturePage'
import EmailFeaturePage from './pages/platform/features/EmailFeaturePage'
import AnalyticsFeaturePage from './pages/platform/features/AnalyticsFeaturePage'

// Who we serve (audience segments; /who-we-help remains the index)
import MusicFestivalsPage from './pages/who-we-help/MusicFestivalsPage'
import ChoralFestivalsPage from './pages/who-we-help/ChoralFestivalsPage'
import YoungArtistProgramsPage from './pages/who-we-help/YoungArtistProgramsPage'
import PreCollegeSummerProgramsPage from './pages/who-we-help/PreCollegeSummerProgramsPage'
import ChurchesSacredMusicPage from './pages/who-we-help/ChurchesSacredMusicPage'
import MusicSchoolsPage from './pages/who-we-help/MusicSchoolsPage'

// Compare
import CompareIndexPage from './pages/compare/CompareIndexPage'
import AcceptdComparePage from './pages/compare/AcceptdComparePage'
import SquarespaceComparePage from './pages/compare/SquarespaceComparePage'
import WixComparePage from './pages/compare/WixComparePage'
import WordPressComparePage from './pages/compare/WordPressComparePage'
import EventbriteComparePage from './pages/compare/EventbriteComparePage'
import SubmittableComparePage from './pages/compare/SubmittableComparePage'
import YapTrackerComparePage from './pages/compare/YapTrackerComparePage'
import IntegrationsIndexPage from './pages/integrations/IntegrationsIndexPage'
import EmailDeliveryIntegrationPage from './pages/integrations/EmailDeliveryIntegrationPage'
import StripeIntegrationPage from './pages/integrations/StripeIntegrationPage'
import GoogleCalendarIntegrationPage from './pages/integrations/GoogleCalendarIntegrationPage'

// Resources
import ResourcesPage from './pages/resources/ResourcesPage'
import GrantsPage from './pages/resources/GrantsPage'

// Legal
import PrivacyPage from './pages/PrivacyPage'
import SmsTermsPage from './pages/SmsTermsPage'
import AccessibilityPage from './pages/AccessibilityPage'

// 404
import NotFoundPage from './pages/NotFoundPage'

// The Router is provided by the harness, NOT here: mountClient() wraps
// <BrowserRouter> on the client and createRenderBody() wraps <StaticRouter> on
// the server (both from @gjl/site-kit). App must stay router-agnostic so the
// same tree renders in both. injectedData is part of the @gjl/site-kit App
// contract; this marketing site carries no CMS payload, so it is ignored.
function App(_props: { injectedData?: unknown } = {}) {
  return (
    // Every animated element on this site is `m.*`, never `motion.*` — see the
    // ADR's note that marketing pages should prefer LazyMotion + m over the
    // full Motion React build. `strict` enforces it: a stray `motion.*` throws
    // at render instead of silently re-adding the full bundle.
    //
    // Features are imported SYNCHRONOUSLY, not via the async `features={() =>
    // import(...)}` form. PageTransition wraps every route in
    // `initial={{ opacity: 0 }}`, so a feature bundle that arrives after first
    // paint risks rendering pages invisible until it lands. A marketing site
    // cannot take that trade for a smaller initial chunk.
    //
    // `domAnimation`, NOT `domMax`. domMax adds the drag + layout feature
    // bundles, and this site used exactly ONE of those features: a single bare
    // `layout` prop on PricingConfigurator's result panel. MEASURED 2026-08-11 —
    // that one prop cost every page 47.4 kB raw / 13.8 kB gz, a third of Motion's
    // entire footprint here. It is now a CSS `interpolate-size` transition
    // (T0, zero runtime) in index.css, so the panel still animates its resize on
    // browsers that support it and snaps on those that do not.
    //
    // Before switching back to domMax, grep for `drag`, `layout` and `layoutId`
    // and confirm something genuinely needs them — one prop is not worth 13.8 kB.
    <LazyMotion features={domAnimation} strict>
      <FeedbackProvider>
        <Routes>
          {/* Standalone pages (no nav) */}
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/call" element={<CallPage />} />

          <Route element={<MainLayout />}>
            {/* Main Pages */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/book" element={<BookPage />} />
            <Route path="/book/cancel" element={<CancelBookingPage />} />
            <Route path="/who-we-help" element={<WhoWeHelpPage />} />
            <Route path="/who-we-help/music-festivals" element={<MusicFestivalsPage />} />
            <Route path="/who-we-help/choral-festivals" element={<ChoralFestivalsPage />} />
            <Route path="/who-we-help/young-artist-programs" element={<YoungArtistProgramsPage />} />
            <Route path="/who-we-help/pre-college-summer-programs" element={<PreCollegeSummerProgramsPage />} />
            <Route path="/who-we-help/churches-sacred-music" element={<ChurchesSacredMusicPage />} />
            <Route path="/who-we-help/music-schools" element={<MusicSchoolsPage />} />

            {/* Compare */}
            <Route path="/compare" element={<CompareIndexPage />} />
            <Route path="/compare/acceptd" element={<AcceptdComparePage />} />
            <Route path="/compare/squarespace" element={<SquarespaceComparePage />} />
            <Route path="/compare/wix" element={<WixComparePage />} />
            <Route path="/compare/wordpress" element={<WordPressComparePage />} />
            <Route path="/compare/eventbrite" element={<EventbriteComparePage />} />
            <Route path="/compare/submittable" element={<SubmittableComparePage />} />
            <Route path="/compare/yaptracker" element={<YapTrackerComparePage />} />
            {/* Integrations — index + a page per integration with a real setup story.
              Thin per-vendor pages read as doorway pages, so the rest stay rows on
              the index (see data/integrations.ts). */}
            <Route path="/integrations" element={<IntegrationsIndexPage />} />
            <Route path="/integrations/email-delivery" element={<EmailDeliveryIntegrationPage />} />
            <Route path="/integrations/stripe" element={<StripeIntegrationPage />} />
            <Route path="/integrations/google-calendar" element={<GoogleCalendarIntegrationPage />} />
            <Route path="/how-we-measure" element={<HowWeMeasurePage />} />
            <Route path="/pay" element={<PayPage />} />
            <Route path="/link-in-bio" element={<LinkInBioPage />} />
            <Route path="/checklist" element={<ChecklistPage />} />
            <Route path="/requests" element={<RequestsPage />} />

            {/* Packages */}
            <Route path="/pricing" element={<PackagesPage />} />

            {/* Portfolio */}
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/portfolio/:slug" element={<PortfolioItemPage />} />

            {/* Blog */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/chroma-festival-case-study" element={<ChromaFestivalCaseStudyPage />} />
            <Route path="/blog/festivals-losing-applicants" element={<FestivalsLosingApplicantsPage />} />
            <Route path="/blog/sierra-canyon-case-study" element={<SierraCanyonCaseStudyPage />} />
            <Route path="/blog/holy-trinity-case-study" element={<HolyTrinityCaseStudyPage />} />
            <Route path="/blog/brass-city-jazz-case-study" element={<BrassCityJazzCaseStudyPage />} />
            <Route path="/blog/wendell-whalum-case-study" element={<WendellWhalumCaseStudyPage />} />
            <Route path="/blog/great-host-composers-case-study" element={<GreatHostComposersCaseStudyPage />} />
            <Route path="/blog/best-website-platforms-for-music-festivals" element={<BestWebsitePlatformsPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />

            {/* Services */}
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/digital-systems" element={<DigitalSystemsPage />} />
            <Route path="/services/websites" element={<WebsitesPage />} />
            <Route path="/services/digital-partnership" element={<DigitalPartnershipPage />} />
            <Route path="/services/done-for-you" element={<DoneForYouPage />} />
            <Route path="/music-manager" element={<MusicManagerPage />} />
            {/* Alias, not a redirect: <Navigate> cannot SSR under the prerenderer; the page's canonical points at /music-manager. */}
            <Route path="/canticle" element={<MusicManagerPage />} />
            <Route path="/services/email-marketing" element={<EmailMarketingPage />} />
            <Route path="/services/donor-management" element={<DonorManagementPage />} />
            <Route path="/services/ticketing" element={<TicketingPage />} />
            <Route path="/services/applications" element={<ApplicationsPage />} />
            <Route path="/services/design" element={<GraphicDesignPage />} />
            <Route path="/services/photography" element={<PhotographyPage />} />

            {/* Platform */}
            <Route path="/platform" element={<PlatformPage />} />
            <Route path="/platform/features/donations" element={<DonationsFeaturePage />} />
            <Route path="/platform/features/website" element={<WebsiteFeaturePage />} />
            <Route path="/platform/features/team" element={<TeamFeaturePage />} />
            <Route path="/platform/features/events" element={<EventsFeaturePage />} />
            <Route path="/platform/features/applications" element={<ApplicationsFeaturePage />} />
            <Route path="/platform/features/email" element={<EmailFeaturePage />} />
            <Route path="/platform/features/analytics" element={<AnalyticsFeaturePage />} />

            {/* Resources */}
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/grants" element={<GrantsPage />} />

            {/* Legal */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </FeedbackProvider>
    </LazyMotion>
  )
}

export default App
